/*
 * bc-custom-length
 *
 * Shows a length input when a specific variant option value is selected
 * (e.g., "Custom Length - In Meters"), or unconditionally on sold-by-length
 * products (data-always-active, from custom.sold_by_length = m/ft). While
 * active:
 *
 *   - the visible quantity selector represents PIECES (number of cuts);
 *   - Add to Cart clicks are intercepted in capture phase and routed
 *     through our own POST to /cart/add.js with `quantity = length × pieces`
 *     and length-per-piece + pieces line item properties (names come from
 *     the global "BC Custom length" theme settings via data attributes;
 *     Horizon's own add-to-cart pipeline strips properties on its
 *     variant-change queue path, so we bypass it entirely in custom mode);
 *   - the same length/pieces properties are ALSO mirrored into real hidden
 *     inputs on the buy-buttons form (see `_syncFormProperties`), because the
 *     accelerated-checkout block's Shop Pay / wallet buttons submit that form
 *     natively and never reach our POST;
 *   - a `bc:custom-length:change` event is dispatched on every change
 *     (consumed by bc-diagram, bc-price-total, and any other
 *     interested listener). Per-piece / total price rewriting on the
 *     main <product-price> element is owned by bc-price-total.
 *
 * Designed to be added to any product template (webbing or otherwise) —
 * the diagram is an optional event consumer, not a dependency.
 */
(() => {
  if (customElements.get('bc-custom-length')) return;

  // Module-level registry. We reparent ourselves into the variant-picker
  // form (see `_anchorToTrigger`) for visual continuity with the trigger
  // option. The variant-picker fetches fresh server HTML on every variant
  // change and morphs (assets/variant-picker.js → morph()), which strips
  // any "extra" old children that aren't in the new HTML — our element
  // included. To survive, we hold a reference to each instance here so
  // it isn't GC'd while disconnected, then re-anchor when `variant:update`
  // fires. Without this, the inputs would vanish on every variant click.
  const REGISTRY = new Set();
  let docListenerInstalled = false;

  // Strip anything that isn't a digit or a single decimal point, and cap
  // precision at one decimal place. Inputs are `type="text"` (Chrome rejects
  // setSelectionRange on type="number", which we need for cursor management),
  // so all character-level validation happens here.
  const sanitizeLengthValue = (val) => {
    let s = String(val ?? '');
    // Comma-decimal locales: treat a single comma as the decimal separator
    // ("1,5" → "1.5"). Stripping it instead would silently read "1,5" as 15 —
    // a ×10 length (and charge). Multiple commas (thousands grouping) still
    // just strip; lengths here are at most 3 digits so that's the rare case.
    if (s.indexOf(',') !== -1 && s.indexOf(',') === s.lastIndexOf(',') && !s.includes('.')) {
      s = s.replace(',', '.');
    }
    s = s.replace(/[^0-9.]/g, '');
    const firstDot = s.indexOf('.');
    if (firstDot === -1) return s;
    const before = s.slice(0, firstDot);
    const after = s.slice(firstDot + 1).replace(/\./g, '').slice(0, 1);
    return `${before}.${after}`;
  };

  // Pieces are whole numbers — strip everything else.
  const sanitizeQtyValue = (val) => String(val ?? '').replace(/[^0-9]/g, '');

  // Clamp the typed value into the merchant-configured [min, max] range
  // on blur. Sanitization runs on every keystroke and only enforces
  // shape (digits + at-most-one-decimal), not bounds — so a customer can
  // freely type "200" mid-edit even when max is 100. Clamping at commit
  // time (blur) gives us valid input without fighting the keystroke.
  // `defaultIfEmpty` covers the empty-string / NaN case so a customer
  // who deletes the value and clicks away gets a sane fallback rather
  // than a 0-or-NaN reaching _customAddToCart.
  const enforceBounds = (input, { isInteger = false } = {}) => {
    const parse = isInteger ? (v) => parseInt(v, 10) : parseFloat;
    const minAttr = parse(input.dataset.min);
    const maxAttr = parse(input.dataset.max);
    const min = Number.isFinite(minAttr) ? minAttr : null;
    const max = Number.isFinite(maxAttr) ? maxAttr : null;
    const defaultIfEmpty = Number.isFinite(min) ? min : isInteger ? 1 : 0.1;
    const raw = parse(input.value);
    let next;
    if (!Number.isFinite(raw) || raw <= 0) {
      next = defaultIfEmpty;
    } else if (min != null && raw < min) {
      next = min;
    } else if (max != null && raw > max) {
      next = max;
    } else {
      next = raw;
    }
    const formatted = String(next);
    if (input.value !== formatted) input.value = formatted;
  };

  // Write a sanitized value back into the input while keeping the caret as
  // close as possible to where the customer was typing. Chars that get
  // stripped sit at-or-just-before the caret, so we shift it back by
  // however many chars were removed in total — close enough for the
  // common single-keystroke case, and never lands the caret past EOL.
  const applySanitizer = (input, sanitizer) => {
    const before = input.value;
    const after = sanitizer(before);
    if (before === after) return;
    const cursorBefore = input.selectionStart ?? after.length;
    input.value = after;
    const removed = before.length - after.length;
    const newCursor = Math.max(0, Math.min(after.length, cursorBefore - removed));
    try {
      input.setSelectionRange(newCursor, newCursor);
    } catch (_e) {
      // Some browsers reject setSelectionRange on certain input states;
      // value is correct either way, so silently skip caret restoration.
    }
  };

  function installDocListener() {
    if (docListenerInstalled) return;
    docListenerInstalled = true;
    document.addEventListener('variant:update', (event) => {
      // Read the new variant ID straight from the event so we don't depend
      // on product-form-component having already written
      // `input[name="id"][ref="variantId"]` by the time our re-anchor runs.
      // The two listeners live on different ancestors (.shopify-section vs.
      // document) and their relative firing order isn't load-bearing once
      // we read from event.detail directly.
      const id = event?.detail?.resource?.id;
      const pendingVariantId = id ? String(id) : null;

      // Dedupe before re-anchoring: each modal open creates a fresh
      // bc-custom-length instance, but the page-level <quick-add-dialog>
      // never disconnects, so disconnectedCallback's home-scope eviction
      // never fires for stale modal instances and they pile up in
      // REGISTRY. Without this pass, the variant:update from a modal
      // variant change re-anchors *every* stale entry, recreating the
      // duplicate inputs the customer just saw.
      //
      // REGISTRY iterates in insertion order, so the latest-added entry
      // per home scope is the current instance — keep that one and drop
      // the rest.
      const latestByScope = new Map();
      for (const el of REGISTRY) {
        if (el.isConnected) continue;
        const scope = el._homeScope;
        if (!scope) continue;
        const prev = latestByScope.get(scope);
        if (prev) REGISTRY.delete(prev);
        latestByScope.set(scope, el);
      }
      for (const el of latestByScope.values()) {
        // Same-scope gate as _onVariantUpdate: don't hand another product's
        // variant id (e.g. from a quick-add modal's picker) to an instance
        // being re-anchored under a different home scope.
        const target = event?.target;
        const sameScope =
          !(target instanceof Element) ||
          !el._homeScope ||
          el._homeScope === document ||
          el._homeScope.contains(target);
        el._reanchorIfPossible(sameScope ? pendingVariantId : null);
      }
    });
  }

  class BcCustomLength extends HTMLElement {
    constructor() {
      super();
      this._onScopeChange = this._onScopeChange.bind(this);
      this._onLengthInput = this._onLengthInput.bind(this);
      this._onVariantUpdate = this._onVariantUpdate.bind(this);
      this._stopChangePropagation = this._stopChangePropagation.bind(this);
      this._onFocusIn = this._onFocusIn.bind(this);
      this._onFocusOut = this._onFocusOut.bind(this);
      this._scheduledSync = null;
      // Which of our inputs ('length' | 'pieces' | null) currently holds
      // focus. Tracked via focusin/focusout so disconnectedCallback can
      // hand it off to the next connectedCallback for focus restoration
      // (see _onFocusOut for the relatedTarget=null carve-out).
      this._focusedKey = null;
    }

    _onFocusIn(event) {
      if (event.target === this.input) this._focusedKey = 'length';
      else if (event.target === this.piecesInput) this._focusedKey = 'pieces';
    }

    _onFocusOut(event) {
      // Clear only when focus moves to a real element outside our inputs.
      // `relatedTarget == null` means either focus is going to <body> OR
      // the focused element is being removed from the DOM. The latter is
      // the variant-picker morph ripping us out (focus is lost as a side
      // effect, not by user intent), and disconnectedCallback needs the
      // stash intact to restore it.
      const next = event.relatedTarget;
      if (next && next !== this.input && next !== this.piecesInput) {
        this._focusedKey = null;
      }
    }

    // The element is anchored inside <variant-picker>, which has a `change`
    // listener on itself that treats any bubbling change event as a variant
    // selection (assets/variant-picker.js → `variantChanged`). When the
    // customer tabs out of the length / pieces input, that listener would
    // fire a spurious fetch + morph, ripping bc-custom-length out of the
    // DOM (and the input out from under the caret). Stopping propagation
    // on these two inputs leaves the radio-button option change events
    // untouched so real variant changes still work.
    _stopChangePropagation(event) {
      event.stopPropagation();
    }

    connectedCallback() {
      this.input = this.querySelector('[data-key="custom-length-input"]');
      this.piecesInput = this.querySelector('[data-key="custom-pieces-input"]');
      if (!this.input) return;

      this.triggerOption = (this.dataset.triggerOption || 'Length').trim();
      this.triggerValue = (this.dataset.triggerValue || '').trim();
      // Always-on mode (products sold by length: custom.sold_by_length = m/ft).
      // No trigger variant exists — _isActive() is unconditionally true and
      // anchoring falls back to the last variant option (or stays at the
      // block position when the product has no real options).
      this.alwaysActive = this.dataset.alwaysActive === 'true';
      this.unitLabel = this.dataset.unitLabel || '';
      this.lengthPropertyName = this.dataset.lengthProperty || 'Piece Length';
      this.piecesPropertyName = this.dataset.piecesProperty || 'Qty';
      // quick-add-dialog wins first so the modal-mounted instance treats
      // the dialog as its persistent host. Without this, scope falls back
      // to `document` and the modal's bc-custom-length would listen for
      // scope changes / quantity updates emitted by the page underneath
      // (and the modal copy must read its own variant payload — the
      // bc-variant-optimistic snippet is rendered inside the morphed
      // product details, not above the section).
      this.scope =
        this.closest('quick-add-dialog') ||
        this.closest('.shopify-section') ||
        this.closest('product-information') ||
        document;

      // Variant lookup payload. Emitted once per section by
      // snippets/bc-variant-optimistic.liquid and shared with the generic
      // optimistic-update component (see assets/bc-variant-optimistic.js).
      // We only consume `prices` (per-piece total math) and `byOptions`
      // (resolving the pending variant id), but the same script feeds both
      // consumers so there's exactly one JSON payload per product page.
      this.variantPrices = {};
      this.variantByOptions = {};
      try {
        const script = this.scope.querySelector('script[data-bc-variants]');
        if (script) {
          const parsed = JSON.parse(script.textContent || '{}');
          this.variantPrices = parsed.prices || {};
          this.variantByOptions = parsed.byOptions || {};
        }
      } catch (_e) {
        // Leave the empty defaults; per-piece math falls back gracefully.
      }

      // Capture the persistent host (product-information / section) so the
      // module-level re-anchor logic can find the trigger fieldset after
      // we get morphed out. The scope outlives variant-picker morphs.
      if (!this._homeScope || !this._homeScope.isConnected) {
        this._homeScope = this.scope;
      }
      REGISTRY.add(this);
      installDocListener();

      // Prune stale predecessors mounted in the same quick-add dialog.
      // The home-scope eviction in disconnectedCallback can't fire for
      // modal instances (the dialog is a page-level singleton, so its
      // isConnected is always true), and the variant:update dedup pass
      // only runs when an event actually fires. Without this sweep, a
      // customer who opens/closes the modal repeatedly without ever
      // changing a variant leaks one instance per open.
      if (this._homeScope?.tagName === 'QUICK-ADD-DIALOG') {
        for (const el of REGISTRY) {
          if (el === this) continue;
          if (el.isConnected) continue;
          if (el._homeScope === this._homeScope) REGISTRY.delete(el);
        }
      }

      // Per-input handlers run sanitization before _sync so the price /
      // diagram / quantity readout always see a clean value. Stored on
      // `this` so disconnectedCallback can remove the same function refs.
      this._onLengthRawInput = () => {
        applySanitizer(this.input, sanitizeLengthValue);
        this._onLengthInput();
      };
      this._onPiecesRawInput = () => {
        applySanitizer(this.piecesInput, sanitizeQtyValue);
        this._onLengthInput();
      };
      // Blur-time clamping: enforce the merchant's data-min / data-max
      // bounds when the customer commits an edit. We can't enforce on
      // every keystroke without thrashing mid-type, so the sanitizer
      // handles shape and this handles range.
      this._onLengthBlur = () => {
        enforceBounds(this.input);
        this._onLengthInput();
      };
      this._onPiecesBlur = () => {
        enforceBounds(this.piecesInput, { isInteger: true });
        this._onLengthInput();
      };

      this.scope.addEventListener('change', this._onScopeChange);
      this.input.addEventListener('input', this._onLengthRawInput);
      this.input.addEventListener('change', this._stopChangePropagation);
      this.input.addEventListener('blur', this._onLengthBlur);
      if (this.piecesInput) {
        this.piecesInput.addEventListener('input', this._onPiecesRawInput);
        this.piecesInput.addEventListener('change', this._stopChangePropagation);
        this.piecesInput.addEventListener('blur', this._onPiecesBlur);
      }

      // Pieces ("Qty") stepper buttons — the +/- that make this box behave
      // like the native quantity selector. Whole-number steps, clamped to
      // the merchant's data-min / data-max. Routed through _stepPieces so the
      // price, diagram, hidden quantity buffer and ARIA all refresh exactly
      // as they do for a typed edit. type="button" + closest() guards mean
      // these never trip the variant-picker change listener or the
      // add-to-cart capture handler below.
      this.piecesMinusBtn = this.querySelector('[data-key="custom-pieces-minus"]');
      this.piecesPlusBtn = this.querySelector('[data-key="custom-pieces-plus"]');
      this._onPiecesMinus = () => this._stepPieces(-1);
      this._onPiecesPlus = () => this._stepPieces(1);
      this.piecesMinusBtn?.addEventListener('click', this._onPiecesMinus);
      this.piecesPlusBtn?.addEventListener('click', this._onPiecesPlus);

      // Select-all on focus for the short numeric inputs. Click in a focused
      // input still places the caret normally. The selectionStart/End guard
      // preserves drag-to-select: if the browser already established a
      // partial range by the time RAF fires, leave it alone.
      const selectAll = (e) => {
        const el = e.currentTarget;
        requestAnimationFrame(() => {
          if (document.activeElement !== el) return;
          if (el.selectionStart !== el.selectionEnd) return;
          el.select();
        });
      };
      this.input.addEventListener('focus', selectAll);
      if (this.piecesInput) this.piecesInput.addEventListener('focus', selectAll);

      // focusin/focusout on the host element track which input is active
      // so disconnectedCallback can preserve it across the variant-picker
      // morph (the morph rips bc-custom-length out of the DOM mid-fetch,
      // which would otherwise silently drop the customer's cursor).
      this.addEventListener('focusin', this._onFocusIn);
      this.addEventListener('focusout', this._onFocusOut);

      this._onQuantityUpdate = () => this._scheduleSync();
      this.scope.addEventListener('quantity-selector:update', this._onQuantityUpdate);
      document.addEventListener('variant:update', this._onVariantUpdate);

      // Block quantity-selector +/- and direct edits while we're driving the
      // cart quantity ourselves — mirrors the cart-side gate so a customer
      // can't desync the underlying quantity field. Scoped tightly to the
      // standard product-page quantity components so unrelated listeners
      // (analytics, third-party apps) on the same event keep firing.
      this._onQuantityCapture = (event) => {
        if (!this._isActive()) return;
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (!this.scope.contains(target)) return;
        if (!target.closest?.('quantity-selector-component, cart-quantity-selector-component')) return;
        event.stopImmediatePropagation();
        this._writeQuantityDisplay();
      };
      document.addEventListener('quantity-selector:update', this._onQuantityCapture, true);

      // Intercept the Add to Cart click. Horizon's add-to-cart pipeline
      // doesn't carry our hidden quantity through its variant-change queue
      // path (it rebuilds the payload from quantity-selector.getValue and
      // omits properties entirely). In custom mode we POST to /cart/add
      // ourselves with the correct cumulative quantity + Length per piece
      // property, then dispatch cart:update so the cart icon/drawer refresh.
      this._onAddToCartClick = (event) => {
        if (!this._isActive()) return;
        if (this._adding) {
          // Already submitting a previous click; suppress until it resolves
          // so a double-click can't double-add the same line.
          event.preventDefault();
          return;
        }
        const target = event.target;
        if (!(target instanceof Element)) return;
        const container = target.closest('add-to-cart-component');
        if (!container || !this.scope.contains(container)) return;
        event.preventDefault();
        // Also stop propagation: Horizon's declarative dispatcher
        // (assets/component.js, document-level bubble listener) fires
        // AddToCartComponent.handleClick — the "Added ✓" / fly-to-cart
        // animation — on every click with no defaultPrevented check, so
        // without this a FAILED custom add would still play the success
        // animation. We re-trigger the button animation ourselves on
        // success (see _customAddToCart).
        event.stopPropagation();
        this._addBtnContainer = container;
        this._customAddToCart();
      };
      document.addEventListener('click', this._onAddToCartClick, true);

      // Accelerated checkout (Shop Pay / wallet buttons) guard.
      //
      // Those buttons submit the buy-buttons form themselves and go straight
      // to a single-line checkout — they never reach _customAddToCart, so
      // they skip its blur-clamp, its stock gate and its same-config fold.
      // The fold doesn't apply (no cart merge on that path) and Shopify's
      // checkout enforces inventory itself, so the only gaps worth closing
      // here are the two that would otherwise send a WRONG line:
      //   1. mid-edit values — commit the same clamp a blur would, so an
      //      out-of-range or empty length can never leave as a real order;
      //   2. an over-stock request on a deny-overselling variant, which
      //      Shopify would silently clamp while our length property still
      //      claimed the full amount (see _customAddToCart's stock gate).
      // The gate is deliberately synchronous — a click handler can't await
      // /cart.js — so it checks this request alone against the page-load
      // stock snapshot rather than the cart-aware projection.
      this._onAcceleratedClick = (event) => {
        if (!this._isActive()) return;
        const target = event.target;
        if (!(target instanceof Element)) return;
        const block = target.closest('.accelerated-checkout-block, .shopify-payment-button');
        if (!block || !this.scope.contains(block)) return;

        if (this.input) enforceBounds(this.input);
        if (this.piecesInput) enforceBounds(this.piecesInput, { isInteger: true });
        this._syncAria();
        // Re-mirrors the hidden property inputs and the quantity display from
        // the clamped values before the wallet reads the form.
        this._sync();

        const length = this._readLength();
        const pieces = this._readPieces();
        const quantity = Math.max(1, Math.ceil(length * pieces));
        const limit = this._stockLimitFor(this._currentVariantId());
        if (limit == null || quantity <= limit) return;

        event.preventDefault();
        event.stopPropagation();
        const amount = (n) => (this.unitLabel ? `${n} ${this.unitLabel}` : String(n));
        this._showAddError(`Only ${amount(limit)} in stock.`);
      };
      document.addEventListener('click', this._onAcceleratedClick, true);

      this._sync();

      // Restore focus if the previous disconnect was a variant-picker
      // morph that ripped us out while a customer was clicked into one
      // of our inputs. _pendingFocusKey is set in disconnectedCallback;
      // restoring here (after _sync has re-shown/anchored everything)
      // puts the caret right back so the customer's click doesn't feel
      // like it was eaten by the page.
      if (this._pendingFocusKey) {
        const key = this._pendingFocusKey;
        this._pendingFocusKey = null;
        const target = key === 'pieces' ? this.piecesInput : this.input;
        target?.focus({ preventScroll: true });
      }
    }

    disconnectedCallback() {
      // Snapshot which input was focused so the next connectedCallback
      // can restore it. _focusedKey is maintained by focusin/focusout;
      // the focusout handler intentionally leaves the stash in place
      // when the focused element is removed (relatedTarget=null) so
      // this read sees the right value even though document.activeElement
      // has already moved to <body> by the time we run.
      this._pendingFocusKey = this._focusedKey;
      this._focusedKey = null;

      this.scope?.removeEventListener('change', this._onScopeChange);
      this.scope?.removeEventListener('quantity-selector:update', this._onQuantityUpdate);
      if (this._onLengthRawInput) this.input?.removeEventListener('input', this._onLengthRawInput);
      this.input?.removeEventListener('change', this._stopChangePropagation);
      if (this._onLengthBlur) this.input?.removeEventListener('blur', this._onLengthBlur);
      if (this._onPiecesRawInput) this.piecesInput?.removeEventListener('input', this._onPiecesRawInput);
      this.piecesInput?.removeEventListener('change', this._stopChangePropagation);
      if (this._onPiecesBlur) this.piecesInput?.removeEventListener('blur', this._onPiecesBlur);
      if (this._onPiecesMinus) this.piecesMinusBtn?.removeEventListener('click', this._onPiecesMinus);
      if (this._onPiecesPlus) this.piecesPlusBtn?.removeEventListener('click', this._onPiecesPlus);
      this.removeEventListener('focusin', this._onFocusIn);
      this.removeEventListener('focusout', this._onFocusOut);
      document.removeEventListener('variant:update', this._onVariantUpdate);
      if (this._onAddToCartClick) document.removeEventListener('click', this._onAddToCartClick, true);
      if (this._onAcceleratedClick) document.removeEventListener('click', this._onAcceleratedClick, true);
      if (this._onQuantityCapture) document.removeEventListener('quantity-selector:update', this._onQuantityCapture, true);
      this._deactivate({ silent: true });

      // Drop ourselves from REGISTRY if our home scope is also gone —
      // this covers the theme-editor authoring case where a block is
      // deleted (or the whole product section is replaced) without a
      // page reload. We defer the check by a microtask because the
      // morph-driven re-anchor path also disconnects us briefly before
      // re-inserting; checking synchronously would evict instances
      // that are about to come back. If we are re-inserted before the
      // microtask fires, `isConnected` will be true and we keep the
      // registration. (Quick-add modal duplicates are pruned by the
      // doc-level variant:update listener instead — the dialog itself
      // is a page-level singleton, so this home-scope check would
      // never evict a stale modal instance.)
      queueMicrotask(() => {
        if (!this.isConnected && !this._homeScope?.isConnected) {
          REGISTRY.delete(this);
        }
      });
    }

    // Gather sibling line item properties (e.g. bc-end-treatment's End 1 /
    // End 2 selectors) from the buy-buttons form so they ride along on our
    // manual /cart/add POST. The standard add-to-cart serialises the form
    // with FormData and would carry these automatically, but custom mode
    // bypasses that pipeline entirely (see the file header), so without this
    // any property set by another block would be silently dropped on
    // custom-length lines. Returns a plain { name: value } map; our own
    // length/pieces properties are layered on top by the caller so they win.
    _collectFormProperties() {
      const props = {};
      const form =
        this.scope.querySelector('form[data-type="add-to-cart-form"]') ||
        this.scope.querySelector('product-form-component form');
      if (!form) return props;
      try {
        const fd = new FormData(form);
        for (const [name, value] of fd.entries()) {
          const match = /^properties\[(.+)\]$/.exec(name);
          if (match && typeof value === 'string' && value !== '') {
            props[match[1]] = value;
          }
        }
      } catch (_e) {
        // FormData can throw on a form detached mid-morph; skip extras then.
      }
      return props;
    }

    // The two length/pieces line item property values, formatted EXACTLY as
    // _customAddToCart formats them for its /cart/add POST. Single source for
    // both paths (our POST and the native form submission below) so a line
    // added through Shop Pay is byte-identical to one added through us —
    // cart-side detection (snippets/cart-products.liquid, assets/bc-cart-length.js)
    // and bc-dashboard's BOM parsing both match on these strings.
    // Returns null while the inputs hold no usable value (mid-edit), so a
    // transient "0 m" can never be submitted.
    _propertyValues() {
      const length = this._readLength();
      const pieces = this._readPieces();
      if (!(length > 0) || !(pieces > 0)) return null;
      return {
        lengthValue: this.unitLabel ? `${length} ${this.unitLabel}` : String(length),
        piecesValue: String(pieces),
      };
    }

    // Mirror the length/pieces properties into REAL hidden inputs on the
    // buy-buttons form.
    //
    // Why: _customAddToCart builds its properties as a JS object and POSTs
    // them itself, so they only ever existed on our own path. The
    // accelerated-checkout block renders {{ form | payment_button }} INSIDE
    // the same buy-buttons form (blocks/buy-buttons.liquid), and Shop Pay /
    // the other wallet buttons submit that form natively — they never see our
    // object, so custom-length orders paid that way reached the cutter with
    // no length or piece count at all. The sewn-loop radios
    // (snippets/bc-end-treatment-group.liquid) DID survive, because they are
    // real form-associated inputs; this makes ours travel the same way.
    //
    // The inputs are our own children and reach the form through the `form`
    // attribute, so the variant-picker morph carries them with this element
    // instead of stripping them (see the REGISTRY comment at the top). A form
    // without an id (shouldn't happen on Horizon) falls back to appending
    // into the form itself.
    //
    // They are written from THIS element's state — the same values
    // _customAddToCart reads — never from the sticky bar, so the sticky-bar
    // mirror inputs (which write our inputs and dispatch 'input', taking us
    // through _sync → _activate) keep them current for free.
    //
    // Off state REMOVES the inputs from the DOM rather than disabling them.
    // `disabled` would be enough for FormData and for a native submit, but the
    // wallet buttons demonstrably do NOT serialise the form that way: the
    // buy-buttons quantity input is disabled in custom mode (see _activate)
    // and yet real Shop Pay orders carry the mirrored length × pieces total,
    // which only happens if the value is read straight off the control
    // (`form.elements.quantity.value` returns "38" while disabled; FormData
    // omits it). A serialiser that ignores `disabled` for quantity would
    // equally ignore it for our properties, so a stale "12.5 m" could ride a
    // non-custom variant to checkout. Removal cannot leak, whichever
    // serialiser is in play.
    _syncFormProperties(enabled) {
      const values = enabled ? this._propertyValues() : null;
      const form =
        this.scope?.querySelector?.('form[data-type="add-to-cart-form"]') ||
        this.scope?.querySelector?.('product-form-component form') ||
        null;

      // getAttribute, NOT `form.id`: the buy-buttons form contains
      // <input name="id"> (the variant id), and a form's named-control getter
      // creates an OWN "id" property that shadows Element.prototype's — so
      // `form.id` hands back that input element, not the form's id string.
      // (Shopify's own storefront observer trips over this: it reports our
      // form as `form#[object htmlinputelement]`.)
      const formId = form?.getAttribute?.('id');

      const write = (key, propertyName, value) => {
        let input = this[key];
        if (value == null) {
          input?.remove();
          this[key] = null;
          return;
        }
        // No form to associate with (mid-morph): drop the input rather than
        // leave an unattached one behind. The next _sync rebuilds it.
        if (!formId) {
          input?.remove();
          this[key] = null;
          return;
        }
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.setAttribute('data-bc-custom-length-prop', '');
          this[key] = input;
        }
        input.name = `properties[${propertyName}]`;
        input.value = value;
        if (input.getAttribute('form') !== formId) input.setAttribute('form', formId);
        if (input.parentNode !== this) this.appendChild(input);
      };

      write('_lengthPropInput', this.lengthPropertyName, values?.lengthValue ?? null);
      write('_piecesPropInput', this.piecesPropertyName, values?.piecesValue ?? null);
    }

    // Per-variant stock ceilings, emitted as data-stock by the block's Liquid.
    // Parsed on demand rather than in connectedCallback: the element is
    // re-rendered by section morphs, and re-reading the attribute each time
    // keeps us off a stale snapshot. Cached against the raw attribute string
    // so repeated adds don't re-parse. A malformed payload yields an empty
    // map — no gate rather than a wrong one; Shopify's own clamp still
    // backstops it.
    _stockMap() {
      const raw = this.getAttribute('data-stock') || '';
      if (this._stockRaw !== raw) {
        this._stockRaw = raw;
        try {
          this._stockParsed = raw ? JSON.parse(raw) : {};
        } catch (_e) {
          this._stockParsed = {};
        }
      }
      return this._stockParsed;
    }

    // The stock ceiling for a variant, or null when it has none. Only
    // inventory-tracked, deny-overselling variants appear in the map, so a
    // missing entry means "unlimited" — never zero.
    _stockLimitFor(variantId) {
      const limit = this._stockMap()[String(variantId)];
      return Number.isFinite(limit) ? Math.max(0, limit) : null;
    }

    async _customAddToCart() {
      if (this._adding) return;
      // Defense-in-depth: blur normally clamps to data-min/data-max, but a
      // programmatic .click() on the add-to-cart button (automation, some
      // assistive-tech focus models) can fire while an out-of-range value is
      // still in a focused input. Re-clamp here so an oversized line never
      // reaches the cart — max_length/max_pieces have no server-side
      // equivalent for continue-selling variants, so this is the last guard.
      if (this.input) enforceBounds(this.input);
      if (this.piecesInput) enforceBounds(this.piecesInput, { isInteger: true });
      this._syncAria();
      // Re-mirror after the clamp so the hidden form inputs can never lag the
      // values this POST is about to send (a programmatic click can land
      // before any blur has run). _collectFormProperties reads them back
      // below; the explicit keys layered on top are the same strings, so the
      // merge is a no-op either way.
      this._syncFormProperties(true);
      // _currentVariantId prefers _pendingVariantId (set synchronously on
      // option change) over the buy-buttons input — product-form only writes
      // the new id into that input AFTER the variant-change fetch resolves,
      // so an Add click landing in that window would otherwise POST the OLD
      // variant id at the new length × pieces quantity.
      this._refreshRefs();
      const variantId = this._currentVariantId();
      const length = this._readLength();
      const pieces = this._readPieces();
      if (!variantId || !(length > 0) || !(pieces > 0)) return;

      this._adding = true;
      try {
        // Shopify cart quantities are integers. Round the TOTAL (length ×
        // pieces) UP to the next whole unit so a fractional remainder
        // never undercharges — e.g. length=24.1, pieces=3 → 72.3 → 73,
        // not 72. Per-piece rounding (ceil(length) × pieces) would charge
        // 75 here, which over-rounds; rounding the total preserves the
        // customer's intent up to one unit of slack.
        const cartQuantity = Math.max(1, Math.ceil(length * pieces));
        const lengthValue = this.unitLabel
          ? `${length} ${this.unitLabel}`
          : String(length);

        const productFormComponent = this.scope.querySelector('product-form-component');
        const productId = productFormComponent?.getAttribute('data-product-id') || '';

        // Merge any sibling properties first, then our length/pieces on top so
        // they always win even if another block reused the same property name.
        const extraProperties = this._collectFormProperties();
        const properties = {
          ...extraProperties,
          [this.lengthPropertyName]: lengthValue,
          [this.piecesPropertyName]: String(pieces),
        };

        const variantIdNum = Number(variantId);
        // ONE /cart.js read serves both the stock projection below and the
        // fold after it — _foldIntoExisting takes this cart rather than
        // fetching its own.
        const cartSnapshot = await window.bcCartAdd.getCart();

        // Stock gate, BEFORE the POST. On a variant that tracks inventory and
        // denies overselling, Shopify accepts an oversized /cart/add and
        // silently CLAMPS the line to what's in stock (200 OK, smaller
        // quantity) while our length property still records the full
        // request — a 40 m line labelled "75 m", wrong for the cutter and
        // for the customer, and reported to neither. So refuse the add
        // instead. We never auto-shrink: the customer asked for a length,
        // not for whatever happens to be left. data-stock is a page-load
        // snapshot, so a concurrent order can still make it stale mid-visit;
        // Shopify's clamp remains the backstop for that rare race, which is
        // the price of not refetching inventory on every add.
        const limit = this._stockLimitFor(variantId);
        if (limit != null) {
          // Every line carrying this variant draws on the same stock,
          // whatever its properties. A null cart (fetch failed) reads as
          // empty here, which reduces this to the cart-blind
          // `cartQuantity <= limit` check — failing open on the cart-aware
          // half beats blocking a legitimate add over an unreachable
          // /cart.js.
          const inCart = (cartSnapshot?.items || [])
            .filter((it) => it.id === variantIdNum)
            .reduce((sum, it) => sum + it.quantity, 0);
          const match = this._findFoldMatch(cartSnapshot, variantIdNum, properties);
          // The fold REPLACES the matched line's quantity rather than adding
          // to it, so project that line from the combined piece count and
          // add the untouched lines back on.
          const projected = match
            ? Math.ceil(length * (this._piecesOnLine(match, length) + pieces)) + (inCart - match.quantity)
            : cartQuantity + inCart;
          if (projected > limit) {
            // "40 m", or a bare "40" where the block carries no unit label.
            const amount = (n) => (this.unitLabel ? `${n} ${this.unitLabel}` : String(n));
            // What MORE the customer could ask for. The fold target counts as
            // already-in-cart even though folding rewrites it, because the
            // fold GROWS that line — anything already on it is spoken for.
            const avail = Math.max(0, limit - inCart);
            let message;
            if (inCart <= 0) {
              message = `Only ${amount(limit)} in stock.`;
            } else if (avail > 0) {
              message = `Only ${amount(avail)} left — your cart already has ${amount(inCart)}.`;
            } else {
              message = `All ${amount(limit)} in stock are already in your cart.`;
            }
            this._showAddError(message);
            return;
          }
        }

        // Fold into an existing same-config line instead of letting
        // Shopify's identical-property merge fire: that merge sums the RAW
        // quantity — METERS here — so a second add of the same config
        // doubled the charge while the pieces property still read the old
        // count. Same policy as bc-segment-builder's _mergeAdd.
        const folded = await this._foldIntoExisting(cartSnapshot, variantIdNum, length, pieces, properties);
        if (folded) {
          this._addBtnContainer?.animateAddToCart?.();
          window.bcCartAdd.dispatchCartUpdate(this, {
            source: 'bc-custom-length',
            cart: folded,
            response: folded,
            itemCount: cartQuantity,
            productId,
            variantId: String(variantId),
          });
          return;
        }

        const { ok, response, cart } = await window.bcCartAdd.add([
          {
            id: Number(variantId),
            quantity: cartQuantity,
            properties,
          },
        ]);

        if (!ok) {
          console.error('[bc-custom-length] cart/add error:', response);
          this._showAddError(
            response.description || response.message || 'Could not add to cart. Please try again.',
            response
          );
          return;
        }

        // Success: play the button's "Added ✓" feedback ourselves — the click
        // handler stopped propagation, so Horizon's declarative dispatcher
        // never ran AddToCartComponent.handleClick.
        this._addBtnContainer?.animateAddToCart?.();

        window.bcCartAdd.dispatchCartUpdate(this, {
          source: 'bc-custom-length',
          cart,
          response,
          itemCount: cartQuantity,
          productId,
          variantId: String(variantId),
        });
      } catch (err) {
        console.error('[bc-custom-length] cart/add fetch failed:', err);
        this._showAddError('Could not add to cart. Please try again.');
      } finally {
        this._adding = false;
      }
    }

    // The cart line this add would fold into: same variant + the SAME
    // properties apart from the pieces count. Shared by the stock projection
    // and _foldIntoExisting so the two can never disagree about which line an
    // add lands on. Null cart / no match → null.
    //
    // 'Sale' is ignored alongside the pieces count: it is stamped from the
    // price at ADD time (snippets/bc-sale-property.liquid renders the form
    // input this path harvests), so a line added before a sale started and the
    // same length added after it would otherwise stop folding and sit in the
    // cart as two identical rows. Shopify charges both at the current price
    // anyway, and _foldIntoExisting's merge lets the newer label win.
    _findFoldMatch(cart, variantId, properties) {
      const cartApi = window.bcCartAdd;
      return (cart?.items || []).find(
        (it) => it.id === variantId
          && cartApi.samePropsIgnoring(it.properties || {}, properties, [this.piecesPropertyName, 'Sale'])
      ) || null;
    }

    // Pieces already on a cart line. A pre-fix merged line may carry a stale
    // pieces property, so the count falls back to quantity ÷ length — the
    // same derivation snippets/cart-products.liquid displays with.
    _piecesOnLine(line, length) {
      return parseInt((line.properties || {})[this.piecesPropertyName], 10)
        || Math.max(1, Math.round(line.quantity / length));
    }

    // Fold into the matching line: change() it to the combined totals —
    // pieces sum, quantity recomputes from ceil(length × pieces), properties
    // rewrite. The cart is passed in (one /cart.js read, shared with the
    // stock gate). Returns the updated cart JSON, or null (no cart / no
    // match / change rejected — the caller then does a plain add, which is
    // no worse than Shopify's native merge).
    async _foldIntoExisting(cart, variantId, length, pieces, properties) {
      const cartApi = window.bcCartAdd;
      if (!cart) return null;
      const match = this._findFoldMatch(cart, variantId, properties);
      if (!match) return null;
      const prevPieces = this._piecesOnLine(match, length);
      const totalPieces = prevPieces + pieces;
      const quantity = Math.max(1, Math.ceil(length * totalPieces));
      const { ok, response } = await cartApi.change({
        id: match.key,
        quantity,
        properties: {
          ...(match.properties || {}),
          ...properties,
          [this.piecesPropertyName]: String(totalPieces),
        },
      });
      return ok ? response : null;
    }

    // Surface a failed custom add the same way product-form.js does: write
    // the message into the buy-buttons error slot (+ live region for screen
    // readers) and auto-hide after 10s. Also dispatch the legacy cart:error
    // event (bridged to shopify:cart:error by bc-events-compat.js) so e.g.
    // the sticky bar resets its button state like any other failed add.
    _showAddError(message, response) {
      const errorEl = this.scope.querySelector('[ref="addToCartTextError"]');
      if (errorEl) {
        errorEl.classList.remove('hidden');
        // Same node layout product-form.js relies on: [ws, svg-wrapper, text].
        const textNode = errorEl.childNodes[2];
        if (textNode) textNode.textContent = message;
        else errorEl.appendChild(document.createTextNode(message));
        if (this._errorTimeout) clearTimeout(this._errorTimeout);
        this._errorTimeout = setTimeout(() => {
          errorEl.classList.add('hidden');
          const liveRegion = this.scope.querySelector('[ref="liveRegion"]');
          if (liveRegion) liveRegion.textContent = '';
        }, 10000);
      }
      const liveRegion = this.scope.querySelector('[ref="liveRegion"]');
      if (liveRegion) liveRegion.textContent = message;
      this.dispatchEvent(
        new CustomEvent('cart:error', {
          bubbles: true,
          detail: {
            sourceId: 'bc-custom-length',
            data: {
              message,
              description: response?.description,
              errors: response?.errors,
            },
          },
        })
      );
    }

    _onScopeChange(event) {
      const t = event.target;
      if (!t || !t.matches) return;
      if (!t.matches('input[type="radio"]') && !t.matches('select')) return;

      // The radio's `change` event fires synchronously on click, but
      // `variant:update` (and the new variant id in `<input name="id">`)
      // only fires after the variant-picker fetch resolves. Resolve the
      // new id from the current option selections so _updateMainPrice can
      // price the per-piece total against the right variant immediately.
      const id = this._resolveVariantIdFromOptions();
      if (id) this._pendingVariantId = id;

      if (!this._isActive()) {
        this._deactivate();
        return;
      }
      // Defer _sync to a microtask so the per-piece overwrite runs after
      // all synchronous `change` listeners in the same scope — notably
      // bc-variant-optimistic, which writes the variant's base price to
      // the same .price slots. Without the defer, listener-registration
      // order would decide whether per-piece or base price wins.
      if (id) queueMicrotask(() => this._sync());
    }

    _resolveVariantIdFromOptions() {
      // Delegate to the shared resolver (assets/bc-variant-resolve.js).
      return window.bcVariantResolve?.resolveVariantId(this.scope, this.variantByOptions) || null;
    }

    _refreshRefs() {
      // Re-query each sync — Horizon may swap the variant id / quantity
      // inputs when the variant changes. Stale references would cause
      // silent failures (wrong variant id, stale pieces value, etc).
      this.variantIdInput = this.scope.querySelector('input[name="id"]');
      this.quantityInput = this.scope.querySelector('input[name="quantity"]');
    }

    _onLengthInput() {
      this._sync();
      this._syncAria();
    }

    // Keep the spinbutton ARIA value in sync with the text inputs so screen
    // readers announce the current length/pieces (the inputs are type="text"
    // for caret control, so they carry role="spinbutton" + aria-value* set in
    // bc-custom-length-input.liquid rather than native number semantics).
    _syncAria() {
      if (this.input) {
        const v = this.input.value;
        this.input.setAttribute('aria-valuenow', v);
        this.input.setAttribute('aria-valuetext', this.unitLabel ? `${v} ${this.unitLabel}` : v);
      }
      if (this.piecesInput) {
        this.piecesInput.setAttribute('aria-valuenow', this.piecesInput.value);
      }
    }

    _onVariantUpdate(event) {
      // Track the authoritative variant id off the event itself —
      // _currentVariantId prefers it over the buy-buttons input, which
      // product-form only rewrites after its own listener runs.
      // ONLY when the event originated inside our own scope: this listener
      // is document-level, so a variant change in a quick-add modal open
      // over this PDP would otherwise store the OTHER product's variant id
      // — and _customAddToCart would then add the wrong product.
      const target = event?.target;
      const sameScope =
        !(target instanceof Element) || this.scope === document || this.scope.contains(target);
      if (sameScope) {
        const id = event?.detail?.resource?.id;
        if (id) this._pendingVariantId = String(id);
      }
      // Re-read after Horizon finishes updating the price/variant DOM.
      this._scheduleSync();
    }

    _scheduleSync() {
      if (this._scheduledSync != null) return;
      this._scheduledSync = requestAnimationFrame(() => {
        this._scheduledSync = null;
        this._sync();
      });
    }

    _findTriggerElement(scope = this.scope) {
      if (!scope) return null;
      // Always-on mode has no trigger option to match — anchor after the
      // LAST variant option so the length input reads as the final step of
      // the option stack (and the morph-survival re-anchor machinery keeps
      // working). Products with only a default variant have no option
      // elements; returning null leaves the input at its block position,
      // which is safe because no variant-picker morph can ever strip it.
      if (this.alwaysActive) {
        const candidates = [
          ...scope.querySelectorAll('fieldset.variant-option'),
          ...scope.querySelectorAll('.variant-option--dropdowns'),
        ];
        return candidates[candidates.length - 1] || null;
      }
      const target = (this.triggerOption || '').toLowerCase();
      const fieldsets = scope.querySelectorAll('fieldset.variant-option');
      for (const fs of fieldsets) {
        const legend = fs.querySelector('legend');
        const text = (legend?.textContent || '').trim().toLowerCase();
        if (text === target || text.startsWith(target)) return fs;
      }
      const dropdowns = scope.querySelectorAll('.variant-option--dropdowns');
      for (const dd of dropdowns) {
        const label = dd.querySelector('label');
        const text = (label?.textContent || '').trim().toLowerCase();
        if (text === target || text.startsWith(target)) return dd;
      }
      return null;
    }

    _anchorToTrigger() {
      // Reparent ourselves into the variant-picker form, directly after the
      // trigger option (e.g. "Length"). Templates render bc-custom-length as
      // a sibling of <variant-picker> by necessity (it's its own block);
      // slotting into the form makes the inputs feel like a continuation of
      // the option they belong to instead of a separate section beneath
      // every option.
      //
      // `.after()` synchronously re-fires connect/disconnect callbacks on
      // this element, so this is only safe to call from `_sync()` — never
      // directly inside connectedCallback — otherwise the outer call's
      // listener-adds would duplicate after the inner reconnect.
      const anchor = this._findTriggerElement();
      if (!anchor) return;
      if (anchor.nextElementSibling === this) return;
      anchor.after(this);
    }

    _reanchorIfPossible(pendingVariantId) {
      // Called by the module-level `variant:update` listener after the
      // variant-picker morph has stripped us from the DOM. The element
      // instance (and its inner inputs with their current values) is
      // preserved by the REGISTRY reference; re-insert it into the form
      // by finding the trigger fieldset under our captured home scope.
      // Bail out cleanly if the scope is gone (e.g., page navigated).
      if (this.isConnected) return;
      const scope = this._homeScope;
      if (!scope || !scope.isConnected) return;
      // Stash the new variant ID from the event so the immediate _sync
      // that runs inside the re-anchor's connectedCallback prices against
      // the new variant, not whatever stale id is still in the buy-buttons
      // input (see _currentVariantId).
      if (pendingVariantId) this._pendingVariantId = pendingVariantId;
      const anchor = this._findTriggerElement(scope);
      if (!anchor) return;
      anchor.after(this);
      // Re-sync on the next frame: the variant-picker fetch that
      // triggered this re-anchor also fires `variant:update`, and
      // product-price / sticky-add-to-cart listeners on that same
      // event morph their price displays back to the server's
      // (per-meter) variant price. If those listeners run after
      // ours, our re-anchor's synchronous sync gets overwritten.
      // Scheduling a follow-up sync ensures the per-piece total wins
      // regardless of listener order.
      this._scheduleSync();
    }

    _readTriggerValue() {
      const trigger = this._findTriggerElement();
      if (!trigger) return '';
      if (trigger.matches('fieldset.variant-option')) {
        const checked = trigger.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : '';
      }
      const select = trigger.querySelector('select');
      return select ? select.value : '';
    }

    _readPieces() {
      // When the local pieces input is rendered, it is the only source of
      // truth — never fall back to the standard quantity input here. In
      // active mode the quantity input holds the cart total (length × pieces)
      // so reading from it would feed runaway totals back into the multiply.
      if (this.piecesInput) {
        const local = parseInt(this.piecesInput.value, 10);
        return Number.isFinite(local) && local > 0 ? local : 1;
      }
      const v = parseInt(this.quantityInput?.value, 10);
      return Number.isFinite(v) && v > 0 ? v : 1;
    }

    // Step the pieces ("Qty") value by ±1 from a stepper button, clamped to
    // the merchant's data-min / data-max. Mirrors the native quantity
    // selector's increment/decrement, then routes through _onLengthInput so
    // the per-piece price, diagram, hidden quantity buffer and ARIA refresh
    // identically to a keyboard edit.
    _stepPieces(delta) {
      if (!this.piecesInput) return;
      const min = parseInt(this.piecesInput.dataset.min, 10);
      const max = parseInt(this.piecesInput.dataset.max, 10);
      const cur = parseInt(this.piecesInput.value, 10);
      // Treat a blank/invalid value as one below min, so a "+" from an empty
      // field lands on min (not min + 1) and "-" stays at min.
      const base = Number.isFinite(cur) ? cur : Number.isFinite(min) ? min - 1 : 0;
      let next = base + delta;
      if (Number.isFinite(min) && next < min) next = min;
      if (Number.isFinite(max) && next > max) next = max;
      const formatted = String(next);
      if (this.piecesInput.value !== formatted) this.piecesInput.value = formatted;
      // _onLengthInput runs _sync (price / diagram / hidden quantity buffer /
      // stepper disabled state via _activate) and _syncAria — the same path a
      // typed edit takes, so a button step and a keystroke are indistinguishable
      // downstream.
      this._onLengthInput();
    }

    // Reflect the clamp bounds on the stepper buttons: disable minus at the
    // minimum and plus at the maximum so the customer gets the same visual
    // dead-end the value already enforces. Called from _activate (which runs
    // on every sync while custom mode is on, covering typed edits too).
    _updateStepperState() {
      if (!this.piecesInput) return;
      const min = parseInt(this.piecesInput.dataset.min, 10);
      const max = parseInt(this.piecesInput.dataset.max, 10);
      const cur = parseInt(this.piecesInput.value, 10);
      if (this.piecesMinusBtn) {
        this.piecesMinusBtn.disabled = Number.isFinite(cur) && Number.isFinite(min) && cur <= min;
      }
      if (this.piecesPlusBtn) {
        this.piecesPlusBtn.disabled = Number.isFinite(cur) && Number.isFinite(max) && cur >= max;
      }
    }

    _writeQuantityDisplay(precomputedTotal) {
      if (!this.quantityInput) return;
      let total = precomputedTotal;
      if (!Number.isFinite(total)) {
        const length = this._readLength();
        const pieces = this._readPieces();
        // Mirror _customAddToCart: ceil the total so the displayed cart
        // quantity matches what gets POSTed at checkout.
        total = length > 0 ? Math.max(1, Math.ceil(length * pieces)) : 1;
      }
      const value = String(total);
      if (this.quantityInput.value !== value) this.quantityInput.value = value;
    }

    _readLength() {
      const v = parseFloat(this.input.value);
      return Number.isFinite(v) && v > 0 ? v : 0;
    }

    _currentVariantId() {
      // _pendingVariantId is the latest selection we know about: set
      // synchronously on option change (_onScopeChange, via the byOptions
      // resolver) and confirmed/corrected on every variant:update
      // (_onVariantUpdate / _reanchorIfPossible). It wins over the
      // buy-buttons input, which product-form-component only rewrites
      // AFTER the variant-change fetch resolves — reading the input alone
      // would return the OLD variant id during that window.
      return this._pendingVariantId || this.variantIdInput?.value || '';
    }

    _currentVariantPriceCents() {
      const id = this._currentVariantId();
      const v = this.variantPrices[id];
      return Number.isFinite(v) ? v : null;
    }

    _isActive() {
      if (this.alwaysActive) return true;
      if (!this.triggerValue) return false;
      const v = this._readTriggerValue();
      return Boolean(v) && v.toLowerCase().trim() === this.triggerValue.toLowerCase();
    }

    _sync() {
      this._refreshRefs();
      this._anchorToTrigger();
      if (this._isActive()) this._activate();
      else this._deactivate();
    }

    _activate() {
      this.removeAttribute('hidden');

      this._updateStepperState();

      const length = this._readLength();
      const pieces = this._readPieces();
      // Use the same ceil rounding _customAddToCart applies so the
      // event detail and the quantity display agree with the eventual
      // cart line quantity.
      const totalQty = length > 0 ? Math.max(1, Math.ceil(length * pieces)) : 0;

      // Flag the scope so CSS can disable the standard quantity selector.
      // Pieces are entered through our local input while custom mode is on.
      if (this.scope?.setAttribute) {
        this.scope.setAttribute('data-bc-custom-length-active', '');
      }
      // Mirror the real cart quantity (length × pieces) into the standard
      // quantity display so the customer can see what they're ordering.
      // Visual only — we drive the cart through _customAddToCart. Floor at
      // 1 so a transient invalid length (mid-edit) doesn't write "0" and
      // trigger Horizon's min-quantity validation.
      this._writeQuantityDisplay(Math.max(1, totalQty));

      // Belt-and-suspenders gate against keyboard edits on the standard
      // quantity input — pointer-events:none only blocks the +/- buttons,
      // and the input itself was previously only "styled" disabled. With
      // the wrapper hidden via CSS this is unreachable in normal UX, but
      // assistive tech or stale CSS could still expose it; readonly +
      // disabled stop edits there too. `disabled` also excludes the input
      // from any unforeseen native form submission paths — we POST the
      // quantity ourselves in _customAddToCart anyway, so dropping this
      // field from the submission is harmless and one fewer way for the
      // cart quantity to desync from length × pieces.
      if (this.quantityInput) {
        this.quantityInput.setAttribute('readonly', '');
        this.quantityInput.setAttribute('aria-readonly', 'true');
        this.quantityInput.setAttribute('disabled', '');
        // Neutralize Horizon's effectiveMax clamp (max - data-cart-quantity)
        // — on a stock-finite product, a subsequent cart:update would
        // otherwise snap the length × pieces total down to the remaining
        // inventory. We drive the cart-line quantity ourselves; the
        // standard input is purely a buffer in custom mode. Stash the
        // server-rendered value first so _deactivate can restore the clamp
        // when the customer switches back to a regular variant.
        if (this._savedCartQuantity === undefined) {
          this._savedCartQuantity = this.quantityInput.getAttribute('data-cart-quantity');
        }
        this.quantityInput.setAttribute('data-cart-quantity', '0');
      }

      // Keep the native-submission mirror (Shop Pay / accelerated checkout)
      // in step with the values above — see _syncFormProperties.
      this._syncFormProperties(true);

      // Price rewrite happens in bc-price-total (subscribes to the
      // event we dispatch below).
      this.dispatchEvent(
        new CustomEvent('bc:custom-length:change', {
          bubbles: true,
          detail: {
            active: true,
            length,
            pieces,
            unit: this.unitLabel,
            totalQty,
          },
        })
      );
    }

    _deactivate({ silent = false } = {}) {
      const wasActive = this.scope?.hasAttribute?.('data-bc-custom-length-active');

      this.setAttribute('hidden', '');

      // Stop the length/pieces properties being serialised on a non-custom
      // variant (and while we're morphed out of the DOM — disconnectedCallback
      // routes here too). `hidden` alone would NOT do this: hidden controls
      // still submit.
      this._syncFormProperties(false);

      if (this.scope?.removeAttribute) {
        this.scope.removeAttribute('data-bc-custom-length-active');
      }

      // Reset the standard quantity input when leaving custom mode — the
      // displayed value was the length × pieces total, which is meaningless
      // for a regular variant.
      if (wasActive) this._refreshRefs();
      if (wasActive && this.quantityInput && this.quantityInput.value !== '1') {
        this.quantityInput.value = '1';
      }
      // Restore editability on the standard quantity input when leaving
      // custom mode — readonly + disabled were applied in _activate.
      if (wasActive && this.quantityInput) {
        this.quantityInput.removeAttribute('readonly');
        this.quantityInput.removeAttribute('aria-readonly');
        this.quantityInput.removeAttribute('disabled');
        // Re-enable Horizon's effectiveMax inventory clamp, zeroed in
        // _activate. The saved value may be stale if the cart changed while
        // custom mode was on, but the next cart:update rewrites it — stale
        // is strictly better than leaving the clamp disabled entirely.
        if (this._savedCartQuantity !== undefined) {
          if (this._savedCartQuantity === null) {
            this.quantityInput.removeAttribute('data-cart-quantity');
          } else {
            this.quantityInput.setAttribute('data-cart-quantity', this._savedCartQuantity);
          }
          this._savedCartQuantity = undefined;
        }
      }

      if (!silent) {
        this.dispatchEvent(
          new CustomEvent('bc:custom-length:change', {
            bubbles: true,
            detail: { active: false },
          })
        );
      }
    }
  }

  customElements.define('bc-custom-length', BcCustomLength);
})();
